import React, { useEffect, useState } from 'react';
import Icon from 'react-native-vector-icons/AntDesign';
import { Modal, Platform, Text, View } from 'react-native';
import { WebView } from 'react-native-webview';
import { get } from 'lodash';

import appConfig from '../../../appConfig.json';
import axiosInstance from '@common/services/axiosIntance/axiosService';
import createStyle from './style';
import { IStamp } from '@common/types';
import {
  STAMP_BACKBOARD_OPTIONS,
  STAMP_IMPRINT_OPTIONS,
} from '@common/constants/stamp.constant';
import { getActions } from '@common/utils/handleActions/func/helps';
import { isCanvas } from '@common/utils/screen';

const API_BASE_URL = appConfig.baseUrl;
const DIGISHOT_DATA_KEY = 'DIGISHOT_DATA';

enum DigishotDataEvent {
  onImprintIssuedOutcome = 'onImprintIssuedOutcome',
  OnTapImprintIssuedOutcomeConfirmation = 'OnTapImprintIssuedOutcomeConfirmation',
  OnTapNoImprintOutcomeConfirmation = 'OnTapNoImprintOutcomeConfirmation',
}

type DigishotDataJson =
  | {
      event:
        | DigishotDataEvent.OnTapImprintIssuedOutcomeConfirmation
        | DigishotDataEvent.OnTapNoImprintOutcomeConfirmation;
    }
  | {
      event: DigishotDataEvent.onImprintIssuedOutcome;
      record: any;
    };

const html = ({
  appId,
  assets,
}: {
  appId: string;
  assets: {
    submittingBackboard: string;
    imprintOutcome: string;
    imprintIssuedOutcomeConfirmation: string;
  };
}) => `
  <!DOCTYPE html>
  <html lang="en">

    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Stamp Push</title>
    </head>

    <body>
      <div id="shotview-container" style="width: 375px; height: 535px; position: absolute; top: 0; left: 0;"> </div>

      <script>
        const postMessage = (data) => {
          if (window?.ReactNativeWebView?.postMessage) {
            window?.ReactNativeWebView?.postMessage(JSON.stringify({ "${DIGISHOT_DATA_KEY}": data }));
          } else {
            // send message back to iframe on web app
            localStorage.setItem("${DIGISHOT_DATA_KEY}", JSON.stringify(data));
          }
        }
        const onDigishotLoaded = () => {
          window.Digishot = window.Digishot || {};
          console.log("=== Digishot loaded");
          try {
            window["Digishot"].Shotview.installPanelDefinitions(function (
              ResourceLoader,
              ShotviewConstants
            ) {
              const panelDef = window["Digishot"].Shotview.createPanelDefinitionBasedOn;

              return {
                _SubmittingBackboard: panelDef("_SubmittingBackboard_v1", {
                  options: {
                    size: "fullSized",
                    base: {
                      // TODO replace with image from stamp display table, use "/base_mikosea01.png" as fallback
                      image: ResourceLoader.preload(
                        "${assets.submittingBackboard}"
                      ),
                      size: "cover",
                      scale: 1,
                    },
                  },
                }),
                _ImprintIssuedOutcome: panelDef("_ImprintIssuedOutcome_v1", {
                  options: {
                    base: {
                      image: ResourceLoader.preload(
                        "${assets.imprintOutcome}"
                      ),
                      position: { x: 0, y: 0 },
                      text: {
                        text: "",
                        fontFamily: "sans-serif",
                        fontSize: 14,
                        fontStyle: "bold",
                        fill: "#AB000C",
                        align: "center",
                        verticalAlign: "middle",
                        lineHeight: 1.1,
                        sv_width: 170,
                        sv_offsetY: 35,
                      },
                    },
                  },
                }),
                _AudioMuteToggleButton: panelDef("_AudioMuteToggleButton_v1", {
                  options: {
                    position: {
                      x: 135,
                      y: -208,
                    },
                    startOnAdd: "off",
                  },
                }),
                _ImprintIssuedOutcomeConfirmation: panelDef(
                  "_ImprintIssuedOutcomeConfirmation_v1",
                  {
                    options: {
                      zLevel: -10,
                      base: {
                        image: ResourceLoader.bundled("${assets.imprintIssuedOutcomeConfirmation}"),
                        position: { x: 0, y: 5 },
                        scale: 0.5,
                      },
                      on: {
                        tap: () => {
                          const digishotDataJson = { event: "${DigishotDataEvent.OnTapImprintIssuedOutcomeConfirmation}" };
                          postMessage(digishotDataJson);
                        },
                      },
                    },
                  }
                ),
                _NoImprintOutcomeConfirmation: panelDef(
                  "_NoImprintOutcomeConfirmation_v1",
                  {
                    options: {
                      on: {
                        tap: () => {
                          const digishotDataJson = { event: "${DigishotDataEvent.OnTapNoImprintOutcomeConfirmation}" };
                          postMessage(digishotDataJson);
                        },
                      },
                    },
                  }
                ),
              };
            });

            const serviceURI = "${API_BASE_URL}/digishot/daas?appId=${appId}";
            const shotview = new (window["Digishot"].Shotview)({
              serviceURI,
              container: "shotview-container",
              userData: {},
              onPrepared: () => {
                console.log("===shotview prepared");
              },
              onImprintIssuedOutcome: (record) => {
                console.log("===onImprintIssuedOutcome", record);
                if (record?.stampcode) {
                  // send message back to iframe on native app
                  const digishotDataJson = { record, event: "${DigishotDataEvent.onImprintIssuedOutcome}" };
                  postMessage(digishotDataJson);
                }
              },
              onNoImprintOutcome: (...args) => {
                // console.log("===onNoImprintOutcome", args);
              },
            });
            return shotview;
          } catch (e) {
            console.error(e);
          }
        };
      </script>
      <script src="${API_BASE_URL}/digishot/digishot/shotview/konva@3.4.1/konva.js"
        onload="console.log('=== Konva loaded')"></script>
      <script src="${API_BASE_URL}/digishot/shotview?appId=${appId}" onload="onDigishotLoaded()"></script>
    </body>
  </html>
`;

const Stamp = (attrs: IStamp) => {
  const styles = createStyle(attrs);
  const [isShow, setIsShow] = useState(false);
  const stampPattern = get(
    attrs,
    'dependencies.appInfor.metadata.digishot.pattern'
  );
  const { backboardImage, zIndex, onPress } = attrs;

  const backboardImageSrc = STAMP_BACKBOARD_OPTIONS.find(
    (o) => o.name === backboardImage
  )?.placeholder;

  const pushStampActionExample = async () => {
    await onPress(getActions(attrs.actions), {
      record: { stampID: 'AA' },
    });
  };

  const handleDigishotData = async (data: DigishotDataJson) => {
    console.log('===handleDigishotData', data);
    switch (data.event) {
      case DigishotDataEvent.onImprintIssuedOutcome:
        if (attrs.validate) {
          axiosInstance({
            method: 'POST',
            url: '/digishot/stamp/validate',
            params: {
              appId: attrs.dependencies.appInfor.appId,
            },
            data: {
              stampID: data.record.stampcode,
              stampType: stampPattern === 'shop-stamp' ? 'shopStamp' : 'rally',
            },
          })
            .then(async (response) => {
              await onPress(getActions(attrs.actions), {
                record: { stampID: data?.record?.stampcode },
              });
            })
            .catch((error) => {
              setIsShow(true);
            });
        } else {
          await onPress(getActions(attrs.actions), {
            record: { stampID: data?.record?.stampcode },
          });
        }

        break;
      case DigishotDataEvent.OnTapImprintIssuedOutcomeConfirmation:
        // TODO: on back
        break;
      case DigishotDataEvent.OnTapNoImprintOutcomeConfirmation:
        // TODO: onTogglePasscodePanel
        break;
    }
  };

  const handleClose = () => {
    setIsShow(false);
  };

  if (isCanvas()) {
    return (
      <View style={{ zIndex, width: attrs.width, height: attrs.height }}>
        <img
          src={
            backboardImageSrc
              ? backboardImageSrc
              : `${appConfig.baseUrl}/digishot/shotview/r/default/koto/base01.png`
          }
          alt="backboard"
        />
      </View>
    );
  }

  useEffect(() => {
    if (Platform.OS === 'web') {
      localStorage.removeItem(DIGISHOT_DATA_KEY);
      let interval = setInterval(() => {
        const data = localStorage.getItem(DIGISHOT_DATA_KEY);
        if (data) {
          const digishotDataJson = JSON.parse(data) as DigishotDataJson;
          handleDigishotData(digishotDataJson);
          clearInterval(interval);
        }
      }, 1000);
      return () => {
        clearInterval(interval);
      };
    }
  }, []);

  return (
    <>
      <View style={styles.container}>
        {/* Replace url with Click stamp endpoint */}
        {/* <Button
        title="Test push stamp"
        onPress={() => {
          pushStampActionExample();
        }}
      /> */}
        <WebView
          style={{ width: 375, height: attrs.height, margin: 'auto' }}
          scrollEnabled={false}
          source={{
            html: html({
              appId: attrs.dependencies.appInfor.appId,
              assets: {
                imprintIssuedOutcomeConfirmation:
                  'https://mikosea.io/next_mikosea.png',
                imprintOutcome:
                  STAMP_IMPRINT_OPTIONS.find(
                    (o) => o.name === attrs.imprintImage
                  )?.placeholder || 'https://mikosea.io/imprint_mikosea01.png',
                submittingBackboard:
                  backboardImageSrc ||
                  'https://mikosea.s3.ap-northeast-1.amazonaws.com/common/stamp_backboard.png',
              },
            }),
          }}
          originWhitelist={['*']}
          javaScriptEnabled
          onMessage={(event) => {
            //check is event from webview or not, only work on mobile app
            const data = event.nativeEvent.data;
            const digishotDataJson = (
              typeof data === 'string' ? JSON.parse(data) : data
            )[DIGISHOT_DATA_KEY] as DigishotDataJson;
            if (digishotDataJson) {
              handleDigishotData(digishotDataJson);
            }
          }}
        />
      </View>
      <Modal
        visible={isShow}
        animationType="fade"
        transparent={true}
        onRequestClose={handleClose}
      >
        <View style={styles.centeredView}>
          <View style={styles.modalView}>
            <View style={styles.modalTop}>
              <Text style={styles.modalTopTitle}>複数のスタンプ</Text>
              <Icon
                style={{
                  marginLeft: 'auto',
                  marginVertical: 'auto',
                }}
                name={'close'}
                size={14}
                onPress={handleClose}
              />
            </View>
            <View style={styles.modalBody}>
              <View style={styles.modalBodyContent}>
                <svg
                  width="96"
                  height="96"
                  viewBox="0 0 96 96"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ margin: 'auto' }}
                >
                  <path
                    d="M61.7721 85L77.271 85C79.3208 85 81.2867 84.1857 82.7362 82.7363C84.1856 81.2868 84.9999 79.321 84.9999 77.2711L84.9999 61.7722C84.9999 59.7186 84.1869 57.7485 82.7386 56.2925C81.2904 54.8364 79.3246 54.0132 77.271 54.0024L61.7721 54.0024C59.7114 54.0024 57.735 54.8208 56.2779 56.278C54.8208 57.7352 54.002 59.7115 54.002 61.7722L54.002 77.2711C54.0128 79.3247 54.8364 81.2905 56.2924 82.7388C57.7484 84.187 59.7185 85 61.7721 85Z"
                    stroke="#6A86EB"
                    stroke-width="6.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M18.7288 85L34.228 85C36.2816 85 38.2516 84.187 39.7076 82.7388C41.1636 81.2905 41.9868 79.3247 41.998 77.2711L41.998 61.7722C41.998 59.7115 41.1792 57.7352 39.722 56.278C38.2648 54.8208 36.2884 54.0024 34.228 54.0024L18.7288 54.0024C16.6752 54.0132 14.7096 54.8364 13.2612 56.2925C11.8132 57.7485 11 59.7186 11 61.7722L11 77.2711C11 79.321 11.8144 81.2868 13.264 82.7363C14.7132 84.1857 16.6792 85 18.7288 85Z"
                    stroke="#6A86EB"
                    stroke-width="6.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M61.7721 41.998L77.271 41.998C79.3246 41.9868 81.2904 41.1636 82.7386 39.7076C84.1869 38.2516 84.9999 36.2816 84.9999 34.228L84.9999 18.7288C84.9999 16.6792 84.1856 14.7132 82.7362 13.2636C81.2867 11.8144 79.3208 11 77.271 11L61.7721 11C59.7185 11 57.7484 11.8128 56.2924 13.2612C54.8364 14.7096 54.0128 16.6752 54.002 18.7288L54.002 34.228C54.002 36.2884 54.8208 38.2648 56.2779 39.722C57.735 41.1792 59.7114 41.998 61.7721 41.998Z"
                    stroke="#6A86EB"
                    stroke-width="6.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                  <path
                    d="M16.4348 36.5848L36.5628 16.4568"
                    stroke="#6A86EB"
                    stroke-width="6.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                  />
                  <path
                    d="M36.5628 36.5412L16.4348 16.4132"
                    stroke="#6A86EB"
                    stroke-width="6.5"
                    stroke-miterlimit="10"
                    stroke-linecap="round"
                  />
                </svg>
                <Text style={{ textAlign: 'center', marginTop: 16 }}>
                  このスタンプは今日すでに <br />
                  使用されています。
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    </>
  );
};

export default Stamp;
